import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { MaskLine, Reveal, FadeIn, Overline } from "@/components/motion";
import { WA_URL } from "@/lib/site";

const FAQ_GROUPS = [
  {
    label: "Prima di prenotare",
    items: [
      {
        q: "Come faccio a sapere se mi serve un esame?",
        a: "Non devi saperlo tu. Raccontaci i sintomi: ti diciamo con sincerità cosa può avere senso fare, anche quando la risposta è che non serve nessun esame. Capita spesso, e preferiamo dirtelo prima.",
      },
      {
        q: "Quanto costa?",
        a: "Le tariffe sono in linea con quelle dei centri privati di Torino. Te le comunichiamo prima di fissare l'appuntamento, così decidi con calma e senza sorprese.",
      },
      {
        q: "Serve l'impegnativa del medico di base?",
        a: "No. Si tratta di prestazioni private, per cui l'impegnativa non è richiesta. Se hai già esami precedenti o una lettera di un altro specialista, portali con te: aiutano a ricostruire il quadro.",
      },
      {
        q: "Ho già fatto altri esami e non hanno trovato nulla. Ha senso venire?",
        a: "Sì. Molte persone arrivano proprio così, dopo mesi di accertamenti senza una risposta chiara. Partiamo da quello che è già stato escluso: è un punto di partenza, non un vicolo cieco.",
      },
      {
        q: "Posso scrivere per un familiare?",
        a: "Certo. Spesso chi si accorge per primo di un problema è un figlio, un genitore o chi dorme accanto. Puoi contattarci tu e organizzare l'appuntamento per un'altra persona.",
      },
    ],
  },
  {
    label: "Le paure che non si dicono",
    items: [
      {
        q: "E se l'esame non trova niente? Mi prenderanno per ansioso?",
        a: "No. Un risultato nella norma è un'informazione, non una perdita di tempo: permette di escludere alcune ipotesi e di smettere di cercare da soli. Chi arriva preoccupato per un sintomo ha una buona ragione per esserlo, e viene preso sul serio.",
      },
      {
        q: "E se invece trovate qualcosa?",
        a: "Te lo spieghiamo con parole semplici: cosa significa, quali scenari sono possibili, quali passi seguono. Non ti lasciamo solo con un referto in mano, e puoi tornare da noi con le domande che nascono dopo.",
      },
      {
        q: "Ho paura che mi prescrivano farmaci che agiscono sul cervello.",
        a: "È un timore che molti pazienti ci raccontano. Un esame serve a capire cosa sta succedendo, non porta automaticamente a una terapia. Se una cura fosse indicata, ne parliamo insieme: benefici, alternative e possibili effetti, poi decidi tu.",
      },
      {
        q: "Mi hanno già detto che è solo stress.",
        a: "Può darsi che sia così, e in molti casi lo è davvero. Ma è una spiegazione che merita di essere verificata, non ripetuta. Ascoltiamo tutta la storia e, se serve, la misuriamo con un esame.",
      },
    ],
  },
  {
    label: "Le visite e gli esami",
    items: [
      {
        q: "Quanto dura la prima visita?",
        a: "Dura il tempo necessario a ricostruire la tua storia clinica e i sintomi nel dettaglio, non quello di uno slot fisso. Il tempo che serve è parte della visita.",
      },
      {
        q: "In quanto tempo ricevo il referto?",
        a: "Elettromiografia: a fine esame. Elettroencefalogramma: 24–48 ore. Poligrafia del sonno: 7 giorni. Polisonnografia: 14 giorni. Poligrafia e polisonnografia includono indicazioni cliniche.",
      },
      {
        q: "La poligrafia del sonno si fa in ospedale?",
        a: "No. Si esegue a casa tua, nel tuo letto, con la strumentazione consegnata e spiegata dal centro. Nessun ricovero.",
      },
    ],
  },
  {
    label: "Prenotare e venire da noi",
    items: [
      {
        q: "Come si prenota?",
        a: "Per telefono, su WhatsApp o con il modulo di questo sito. La richiesta dal sito non è una prenotazione automatica: ti proponiamo noi un orario entro 24 ore, senza impegno.",
      },
      {
        q: "Dove siete?",
        a: "In Via Avigliana 38, a Torino. Siamo a quattro traverse dalla stazione di Torino Porta Susa: dieci minuti a piedi, tre o quattro in auto.",
      },
      {
        q: "Se devo disdire o spostare?",
        a: "Basta avvisarci in tempo, per telefono o su WhatsApp, e riprogrammiamo insieme.",
      },
      {
        q: "Posso venire accompagnato?",
        a: "Sì. Se ti fa sentire più tranquillo, o se accompagni un genitore, non c'è nessun problema.",
      },
    ],
  },
  {
    label: "Dopo l'esame",
    items: [
      {
        q: "Cosa succede dopo che ho il referto?",
        a: "Il referto degli esami del sonno include indicazioni cliniche. Se hai bisogno di capire meglio cosa significa, o di decidere i passi successivi, puoi tornare da noi: non finisce con la consegna.",
      },
      {
        q: "Se ho un dubbio dopo la visita, a chi scrivo?",
        a: "Allo studio, per telefono o su WhatsApp. Restare disponibili nel tempo è uno dei tre impegni su cui è nato Synapses.",
      },
    ],
  },
];

function FaqGroup({ group, gi }) {
  const warm = gi % 2 === 1;
  return (
    <section className={`${warm ? "bg-crema-warm" : "bg-crema"} py-16 lg:py-24`} data-testid={`faq-group-${gi}`}>
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        <Reveal>
          <Overline className="mb-8">{group.label}</Overline>
        </Reveal>
        <Reveal delay={0.05}>
          <Accordion type="single" collapsible className="w-full">
            {group.items.map((f, i) => (
              <AccordionItem
                key={i}
                value={`faq-${gi}-${i}`}
                className="border-b border-viole/20"
                data-testid={`faq-item-${gi}-${i}`}
              >
                <AccordionTrigger
                  className="text-left font-sora font-semibold tracking-tight text-lg md:text-xl text-ink py-7 hover:text-indaco hover:no-underline transition-colors duration-300"
                  data-testid={`faq-trigger-${gi}-${i}`}
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[17px] md:text-lg text-viole leading-relaxed pb-7 max-w-2xl">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </section>
  );
}

export default function Faq() {
  return (
    <>
      <section className="bg-crema pt-40 pb-16 lg:pt-52 lg:pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <FadeIn>
            <Overline>Domande frequenti</Overline>
          </FadeIn>
          <h1 className="mt-8 font-sora font-extrabold tracking-tighter text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-ink" data-testid="faq-title">
            <MaskLine delay={0.15}>Le risposte,</MaskLine>
            <MaskLine delay={0.3}>
              <span className="text-indaco">prima ancora di chiederle.</span>
            </MaskLine>
          </h1>
          <FadeIn delay={0.6}>
            <p className="mt-8 max-w-2xl text-viole text-lg leading-relaxed">
              Anche quelle che non si fanno volentieri. Se la tua domanda non è qui,
              scrivici: ti rispondiamo lo stesso.
            </p>
          </FadeIn>
        </div>
      </section>

      {FAQ_GROUPS.map((g, gi) => (
        <FaqGroup key={g.label} group={g} gi={gi} />
      ))}

      <section className="bg-indaco-deep text-crema py-24 lg:py-32">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <h2 className="font-sora font-bold tracking-tighter text-3xl md:text-5xl">
              Non hai trovato la risposta?
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-6 text-crema/70 text-lg">
              Scrivici la tua domanda, anche in due righe. Ti rispondiamo noi entro 24 ore, senza impegno.
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link
                to="/prenota"
                className="group inline-flex items-center gap-3 rounded-full bg-crema px-8 py-4 text-[15px] md:text-base font-semibold text-ink transition-colors duration-300 hover:bg-arancio hover:text-crema"
                data-testid="faq-cta-prenota"
              >
                Richiedi un appuntamento
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <a
                href={WA_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 rounded-full border border-crema/30 px-8 py-4 text-[15px] md:text-base font-semibold text-crema transition-colors duration-300 hover:border-crema"
                data-testid="faq-cta-whatsapp"
              >
                <MessageCircle className="w-4 h-4" />
                WhatsApp
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
